import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { HeartIcon } from '@heroicons/react/24/outline';
import { CompanyApi } from '../../../../../api/companyApi';
import {
  errorToast,
  successToast,
  getLocalStorageItem,
  isCopanySubAdmin
} from '../../../../../utils/helper';
import Loader from '../../../../../component/common/Loader';
import Topbar from '../../../../PulseSurvey/Topbar';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import ToggleSwitch from '../../../../../component/common/ToggleSwitch';
import {
  StandardButton,
  StandardDropdown,
  EmptyState
} from '../../../../../component/common/StandardUI';
import AddKeyDateModal from '../components/AddKeyDateModal';
import aiSparkIcon from '../../../../../assets/svg/icons/Ai-Spark-Starlight--Streamline-Flex.svg';

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const MONTH_OPTIONS = MONTHS.map((label, index) => ({ label, value: index + 1 }));

/**
 * A key date is past once its day has gone by. Whole-month events (no day) only
 * count as past once the whole month has. Past events render greyed out; today
 * and anything ahead stay white.
 */
const isPastKeyDate = (keyDate) => {
  const now = new Date();
  const thisYear = now.getFullYear();
  const thisMonth = now.getMonth() + 1;

  if (keyDate.year !== thisYear) return keyDate.year < thisYear;
  if (keyDate.month !== thisMonth) return keyDate.month < thisMonth;
  return keyDate.day ? keyDate.day < now.getDate() : false;
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear - 1, currentYear, currentYear + 1].map((year) => ({
  label: `${year}`,
  value: year
}));

const KeyDates = () => {
  const userData = JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;
  const isSubAdmin = isCopanySubAdmin();
  const pages = [
    { name: 'People plan', href: `/${slug}/people-plan`, current: false },
    { name: 'Key dates', href: `/${slug}/people-plan/key-dates`, current: true }
  ];

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [keyDates, setKeyDates] = useState([]);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchKeyDates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await CompanyApi.getKeyDates(year, month, 1, 50);
      if (response?.data?.meta?.code === 1) {
        setKeyDates(response?.data?.data?.keyDates || []);
        setAiEnabled(!!response?.data?.data?.aiEnabled);
      } else {
        setKeyDates([]);
      }
    } catch (error) {
      errorToast(error.message || 'Failed to load key dates');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchKeyDates();
  }, [fetchKeyDates]);

  const handleAiToggle = async (value) => {
    try {
      const response = await CompanyApi.toggleKeyDatesAi(year, month, value);
      if (response?.data?.meta?.code === 1) {
        fetchKeyDates();
      } else {
        setAiEnabled(!value);
        errorToast(response?.data?.meta?.message || 'Failed to update AI suggestions');
      }
    } catch (error) {
      setAiEnabled(!value);
      errorToast(error.message || 'Failed to update AI suggestions');
    }
  };

  const handleAddCustom = async (values) => {
    try {
      setSaving(true);
      const response = await CompanyApi.addCustomKeyDate({ ...values, year, month });
      if (response?.data?.meta?.code === 1) {
        successToast(response?.data?.meta?.message || 'Key date added successfully');
        setIsModalOpen(false);
        fetchKeyDates();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to add key date');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to add key date');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative lg:mb-[45px]">
      {loading && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>Globally significant key dates | Shoorah Admin</title>
      </Helmet>
      <Topbar title="Globally significant key dates" infoBtn={false} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        <div className="flex flex-wrap items-center justify-end gap-3 mb-6">
          <StandardDropdown
            label="Select year"
            options={YEAR_OPTIONS}
            value={year}
            onChange={setYear}
            align="right"
            variant="secondary"
            buttonClassName="bg-white dark:bg-shoorah-darkBgTabColor"
          />
          {!isSubAdmin && <StandardButton text="Add custom" onClick={() => setIsModalOpen(true)} />}
        </div>

        <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-medium text-gray-700 dark:text-gray-200">{year}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Key dates for wellbeing, mental health, and your workplace.
              </p>
            </div>
            <StandardDropdown
              label="Select month"
              options={MONTH_OPTIONS}
              value={month}
              onChange={setMonth}
              align="right"
              variant="secondary"
              buttonClassName="bg-white dark:bg-shoorah-darkBgTabColor"
            />
          </div>

          <div className="mt-5 max-w-md rounded-input border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  AI Suggestion
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Get AI-powered recommendations tailored to your company for each global awareness
                  day.
                </p>
              </div>
              <ToggleSwitch
                toggleValue={aiEnabled}
                setToggleValue={setAiEnabled}
                onChange={handleAiToggle}
                disabled={isSubAdmin}
                className="!bg-transparent !border-shoorah-btn dark:!border-shoorah-btnDark p-0.5"
                switchClassName="!h-4 !w-4 !bg-shoorah-btn dark:!bg-shoorah-btnDark"
              />
            </div>
          </div>

          <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 mt-6 mb-4">
            {MONTHS[month - 1]}, {year}
          </h3>

          {!loading && keyDates.length === 0 ? (
            <EmptyState
              size="lg"
              title="No key dates for this month"
              description="Add a custom key date such as a team birthday, holiday or company event."
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {keyDates.map((keyDate) => {
                const isPast = isPastKeyDate(keyDate);
                return (
                <div
                  key={keyDate._id}
                  className={`rounded-input border border-shoorah-borderGray dark:border-shoorah-darkBorder p-4 ${
                    isPast
                      ? 'bg-shoorah-normanGray dark:bg-shoorah-darkBgColor'
                      : 'bg-white dark:bg-shoorah-darkBgTabColor'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <HeartIcon className="w-6 h-6 text-gray-700 dark:text-gray-200 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {keyDate.title}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        <span className="font-medium text-gray-700 dark:text-gray-200">Date:</span>{' '}
                        {keyDate.dateLabel || 'Whole month'}
                      </p>
                    </div>
                  </div>

                  {keyDate.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-3">
                      <span className="font-medium text-gray-700 dark:text-gray-200">
                        Description:
                      </span>{' '}
                      {keyDate.description}
                    </p>
                  )}

                  {aiEnabled && keyDate.aiSuggestion?.isEnabled && keyDate.aiSuggestion?.text && (
                    <div
                      className={`mt-4 rounded-input border border-shoorah-borderGray dark:border-shoorah-darkBorder p-4 ${
                        isPast
                          ? 'bg-white dark:bg-shoorah-darkBgTabColor'
                          : 'bg-shoorah-normanGray dark:bg-shoorah-darkBgColor'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <img src={aiSparkIcon} alt="" className="w-5 h-5 shrink-0 dark:invert" />
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          AI Suggestion
                        </p>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                        {keyDate.aiSuggestion.text}
                      </p>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AddKeyDateModal
        open={isModalOpen}
        monthLabel={`${MONTHS[month - 1]} ${year}`}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddCustom}
        submitting={saving}
      />
    </div>
  );
};

export default KeyDates;
